import { Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  Companies,
  Conditions,
  Order,
  Orders,
  Personen,
  Staff,
  WebserviceResponse
} from "../../../_shared/interfaces";
import { ClarityModule, ClrInputModule, ClrSelectModule, ClrStepperModule } from "@clr/angular";
import { DatePipe, JsonPipe, NgForOf, NgIf } from "@angular/common";
import { Router, RouterLink } from "@angular/router";
import {
  AlertService,
  ConditionStoreService, ContactsStoreService, IAlertType,
  OrdersStoreService,
  PersonsStoreService,
  StaffStoreService
} from "../../../_shared/services";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import { RequestCompanySearchComponent } from "../../requests/request-company-search/request-company-search.component";

import { environment as env } from "../../../../environments/environment";
import {TranslocoDirective} from "@jsverse/transloco";

import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';
import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-order-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    ClrStepperModule,
    ReactiveFormsModule,
    ClrInputModule,
    ClrSelectModule,
    NgIf,
    ClarityModule,
    DatePipe,
    RouterLink,
    NgForOf,
    JsonPipe,
    RequestCompanySearchComponent,
    TranslocoDirective
  ],
  templateUrl: './order-form.component.html',
  styleUrl: './order-form.component.sass',
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})
export class OrderFormComponent implements OnInit, OnChanges {
  orderForm!: FormGroup;
  private defaultDate: Date = new Date();
  persons: Personen[] = [];
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.orderForm);
  }
  delivery_conditions: Conditions[] = [];
  payment_conditions: Conditions[] = [];
  delivery_address: Companies[] = [];

  @Input() projectId: string = '';
  @Input() jobId: number = 0;
  @Input() orderId: number = 0;
  @Input() ordersData?: Orders;
  @Input() isUpdateMode = true;
  @Output() formSubmit = new EventEmitter<Order>();

  devMode: boolean = false;

  constructor(
    private personsService: PersonsStoreService,
    private staffService: StaffStoreService,
    private condss: ConditionStoreService,
    private ordss: OrdersStoreService,
    private contss: ContactsStoreService,
    private alertDispatcher: AlertService,
    private router: Router,
    private fb: FormBuilder,
    private datePipe: DatePipe,
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon);
    this.devMode = env.stages.dev;
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ordersData'] && this.ordersData) {
      this.patchFormValues(this.ordersData);
    }
  }

  ngOnInit(): void {
    this.loadStaff();
    this.loadPayConds();
    this.loadDeliveryConds();
  }

  deleteOrder() {}

  private initForm() {
    this.orderForm = this.fb.group({
      stepI: this.fb.group({
        project_id: [''],
        order_cid: [''],
        company_name: [''],
        order_street: [''],
        order_postcode: [''],
        order_city: [''],
        order_country: [''],
        order_status: [''],
        order_subject: new FormControl('', [Validators.required, Validators.maxLength(255)]),
        order_date: new FormControl(this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy')),
        order_delivery: new FormControl(this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy')),
        order_confirmation_id: [''],
        purchasing_price: 0,
        selling_price: 0
      }),
      stepII: this.fb.group({
        employee_phm: [''],
      }),
      stepIII: this.fb.group({
        employee_order: [''],
      }),
      stepIV: this.fb.group({
        cond_payment: [''],
        cond_delivery: [''],
      }),
      stepV: this.fb.group({
        company_delivery: [''],
        person_delivery: [''],
      })
    });
  }

  private patchFormValues(data: Orders) {
    if (!data) return;

    const formValue = {
      stepI: {
        project_id: data.project_id || '',
        order_cid: data.order_cid || '',
        company_name: data.company_name || '',
        order_street: data.order_street || '',
        order_postcode: data.order_postcode || '',
        order_city: data.order_city || '',
        order_country: this.mapCountryForForm(data.order_country || ''),
        order_status: data.order_status || '',
        order_subject: data.order_subject || '',
        order_date: this.datePipe.transform(data.order_date, 'dd.MM.yyyy') || '',
        order_delivery: this.datePipe.transform(data.order_delivery, 'dd.MM.yyyy') || '',
        order_confirmation_id: data.order_confirmation_id || '',
        purchasing_price: data.purchasing_price || 0,
        selling_price: data.selling_price || 0
      },
      stepII: {
        employee_phm: data.employee_phm || '',
      },
      stepIII:{
        employee_order: data.employee_order || '',
      },
      stepIV: {
        cond_payment: data.cond_payment || '',
        cond_delivery: data.cond_delivery || '',
      },
      stepV: {
        company_delivery: data.company_delivery || '',
        person_delivery: data.person_delivery || '',
      }
    };
    this.loadPersons(data.order_cid);
    this.loadDeliveryAddress(data.company_delivery);
    this.orderForm.patchValue(formValue);
  }

  splitProjectNumber(projectId: string, substring: number) {
    var splits = projectId.split('.');
    return splits[substring];
  }

  disableStepII() {
    let disable: boolean = false;
    if (this.orderForm) {
      if ((this.orderForm.value.stepI.order_confirmation_id.length < 2) && ((this.orderForm.value.stepI.order_status == 'confirmed') || (this.orderForm.value.stepI.order_status == 'running') || (this.orderForm.value.stepI.order_status == 'invoiced') || (this.orderForm.value.stepI.order_status == 'done'))) disable = true;
    }
    return disable
  }

  fillRecipient(supplier: Companies) {
    this.orderForm.patchValue({
      stepV: {
        company_delivery: supplier.cid,
      }
    });

    this.delivery_address = [{
      ...supplier,
      country: this.mapCountryForForm(supplier.country),
    }];
  }

  private mapCountryForForm(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (!trimmed) return '';
    const upper = trimmed.toUpperCase();
    if (upper === 'DE' || trimmed === 'Deutschland' || trimmed === 'Germany') return 'DE';
    if (upper === 'AT' || trimmed === 'Österreich' || trimmed === 'Austria') return 'AT';
    if (upper === 'CH' || trimmed === 'Schweiz' || trimmed === 'Switzerland') return 'CH';
    return trimmed;
  }

  projectStaff(): number {
    return this.orderForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  loadPersons(companyId: number){
    this.personsService.getPersonInCompany(companyId, true).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        // Sortiere die Liste nach dem Nachnamen
        this.persons.sort((a, b) => {
          if (a.lastname < b.lastname) {
            return -1;
          }
          if (a.lastname > b.lastname) {
            return 1;
          }
          return 0;
        });

      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Kunden: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadPayConds(){
    this.condss.getConditionByType(2).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.payment_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadDeliveryConds(){
    this.condss.getConditionByType(1).subscribe({
      next: (data: WebserviceResponse<Conditions[]>) => {
        if (data?.object) {
          this.delivery_conditions = data.object;
        }
      },
      complete: () => {},
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Bedingungen: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }

  loadDeliveryAddress(contactID: number){
    this.contss.getSingleCompany(contactID)
      .subscribe(address => {
        // console.table(condition);
        if (!address){
          return
        }
        this.delivery_address = address.object;
      })
  }

  updateOrder() {
    if (this.orderForm.valid) {
      const formValue = this.orderForm.value;
      this.ordss.updateOrder(formValue, this.jobId, this.orderId).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: 'Order erfolgreich aktualisiert',
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(formValue);
          this.router.navigate(['/order/details', this.projectId, this.orderId]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren der Order: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }

  get isOrderValid(): boolean {
    return !!(this.ordersData &&
      this.ordersData.cond_payment > 0 &&
      this.ordersData.cond_delivery > 0);
  }

  get selectedDeliveryHasOption(): boolean {
    const condId = Number(this.orderForm.value?.stepIV?.cond_delivery);
    if (!condId) return false;
    const cond = this.delivery_conditions.find(c => c.cond_id === condId);
    return cond?.option ?? false;
  }

}
